import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Search,
  Filter,
  Plus,
  Edit2,
  Trash2,
  X,
} from 'lucide-react';
import {
  AdminClientApi,
  InviteWithCoupon,
  GetInvitesInputInterface,
  CreateInviteInputInterface,
  ClientCouponInterface,
} from '../../Admin-ClientLibrary';
import { getApiUrl } from '../../config/api';
import { createInviteSchema, CreateInviteFormData } from '../../schemas/invite.schema';
import './Invites.css';

const getToken = (): string => {
  return localStorage.getItem('authToken') || '';
};

const adminApi = new AdminClientApi(getApiUrl(), getToken);

const Invites: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [invites, setInvites] = useState<InviteWithCoupon[]>([]);
  const [coupons, setCoupons] = useState<ClientCouponInterface[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>(searchParams.get('search') || '');
  const [totalInvites, setTotalInvites] = useState<number>(0);
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [inviteToDelete, setInviteToDelete] = useState<InviteWithCoupon | null>(null);
  const [createLoading, setCreateLoading] = useState<boolean>(false);

  const [filters, setFilters] = useState({
    is_active: searchParams.get('is_active') || '',
  });

  // React Hook Form for Create Invite
  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<CreateInviteFormData>({
    resolver: zodResolver(createInviteSchema),
    defaultValues: {
      name: '',
      description: '',
      coupon_id: undefined,
    },
  });

  const limit = 20;

  const updateUrlParams = (params: Record<string, string | number | undefined>): void => {
    const newParams = new URLSearchParams();
    searchParams.forEach((value, key) => {
      newParams.set(key, value);
    });
    Object.entries(params).forEach(([key, value]) => {
      if (value && value !== '') {
        newParams.set(key, value.toString());
      } else {
        newParams.delete(key);
      }
    });
    setSearchParams(newParams);
  };

  const fetchCoupons = async (): Promise<void> => {
    try {
      const response = await adminApi.getCoupons({ limit: 100, is_active: true });
      setCoupons(response.coupons);
    } catch (err: any) {
      console.error('Error fetching coupons:', err);
    }
  };

  const fetchInvites = async (
    search: string = '',
    filterParams = filters,
    shouldGetCount: boolean = false
  ): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      
      const apiParams: GetInvitesInputInterface = {
        limit,
        search,
        getCount: shouldGetCount,
      };

      if (filterParams.is_active) {
        apiParams.is_active = filterParams.is_active === 'true';
      }

      const response = await adminApi.getInvites(apiParams);
      setInvites(response.invites);

      if (shouldGetCount && response.total !== undefined) {
        setTotalInvites(response.total);
      }
    } catch (err: any) {
      console.error('Error fetching invites:', err);
      setError(err.message || 'Failed to fetch invites');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCoupons();
  }, []);

  useEffect(() => {
    const urlSearch = searchParams.get('search') || '';
    const urlFilters = {
      is_active: searchParams.get('is_active') || '',
    };

    setSearchTerm(urlSearch);
    setFilters(urlFilters);
    setCurrentPage(1);

    fetchInvites(urlSearch, urlFilters, true);
  }, [searchParams]);

  useEffect(() => {
    const state = location.state as { refresh?: boolean } | null;
    if (state?.refresh) {
      fetchInvites(searchTerm, filters, true);
      navigate(location.pathname + location.search, { replace: true, state: {} });
    }
  }, [location.state]);

  const handleSearch = (e: React.FormEvent): void => {
    e.preventDefault();
    updateUrlParams({
      search: searchTerm,
      is_active: filters.is_active,
    });
  };

  const handleFilterChange = (filterName: string, value: string): void => {
    setFilters({ ...filters, [filterName]: value });
  };

  const applyFilters = (): void => {
    updateUrlParams({
      search: searchTerm,
      is_active: filters.is_active,
    });
    setShowFilters(false);
  };

  const clearFilters = (): void => {
    updateUrlParams({
      is_active: '',
    });
    setShowFilters(false);
  };

  const formatDiscount = (invite: InviteWithCoupon): string => {
    if (!invite.coupon) return 'No coupon';
    if (invite.coupon.percent_off) {
      return `${invite.coupon.percent_off}% off`;
    }
    if (invite.coupon.amount_off) {
      return `$${(invite.coupon.amount_off / 100).toFixed(2)} off`;
    }
    return invite.coupon.name;
  };

  const formatDate = (date: Date | undefined): string => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString();
  };

  const handleRowClick = (inviteId: number, event: React.MouseEvent): void => {
    const target = event.target as HTMLElement;
    if (target.tagName === 'BUTTON' || target.closest('button') || target.closest('.actions')) {
      return;
    }
    navigate(`/invites/${inviteId}`);
  };

  const onCreateInvite = async (data: CreateInviteFormData): Promise<void> => {
    try {
      setCreateLoading(true);
      
      const inviteData: CreateInviteInputInterface = {
        name: data.name,
        description: data.description || undefined,
        coupon_id: data.coupon_id || undefined,
      };

      const response = await adminApi.createInvite(inviteData);

      if (response.success) {
        setShowCreateModal(false);
        reset();
        fetchInvites(searchTerm, filters, true);
      } else {
        alert(response.message || 'Failed to create invite');
      }
    } catch (err: any) {
      console.error('Error creating invite:', err);
      alert(err.message || 'Failed to create invite');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleCloseCreateModal = (): void => {
    setShowCreateModal(false);
    reset();
  };

  const handleDeleteClick = (invite: InviteWithCoupon, event: React.MouseEvent): void => {
    event.stopPropagation();
    setInviteToDelete(invite);
    setShowDeleteModal(true);
  };

  const confirmDelete = async (): Promise<void> => {
    if (!inviteToDelete) return;

    try {
      const response = await adminApi.deleteInvite({ id: inviteToDelete.id });

      if (response.success) {
        setShowDeleteModal(false);
        setInviteToDelete(null);
        fetchInvites(searchTerm, filters, true);
      } else {
        alert(response.message || 'Failed to delete invite');
      }
    } catch (err: any) {
      console.error('Error deleting invite:', err);
      alert(err.message || 'Failed to delete invite');
    }
  };

  const totalPages = totalInvites > 0 ? Math.ceil(totalInvites / limit) : 1;

  const getFilterSummary = (): string => {
    const parts: string[] = [];
    
    if (filters.is_active) {
      parts.push(`Status: ${filters.is_active === 'true' ? 'Active' : 'Inactive'}`);
    }

    if (parts.length === 0) {
      return `Total: ${totalInvites} invite${totalInvites !== 1 ? 's' : ''}`;
    }

    return `${parts.join(', ')} | Total: ${totalInvites}`;
  };

  return (
    <div className="invites-page">
      <div className="invites-header">
        <div className="header-top">
          <h1>Invites</h1>
          <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
            <Plus size={16} />
            Create Invite
          </button>
        </div>

        <div className="invites-controls">
          <div className="controls-row">
            <form onSubmit={handleSearch} className="search-form">
              <div className="search-input">
                <Search size={16} />
                <input
                  type="text"
                  placeholder="Search invites..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <button type="submit" className="btn-secondary">
                Search
              </button>
            </form>

            <button className="btn-secondary" onClick={() => setShowFilters(!showFilters)}>
              <Filter size={16} />
              Filter{' '}
              {filters.is_active && <span className="filter-badge">•</span>}
            </button>
          </div>

          <div className="filter-summary">{getFilterSummary()}</div>

          {showFilters && (
            <div className="filters-dropdown">
              <div className="filters-header">
                <h3>Filters</h3>
                <button className="btn-text" onClick={clearFilters}>
                  Clear All
                </button>
              </div>

              <div className="filter-group">
                <label>Status:</label>
                <select
                  value={filters.is_active}
                  onChange={(e) => handleFilterChange('is_active', e.target.value)}
                >
                  <option value="">All</option>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>

              <div className="filters-actions">
                <button className="btn-secondary" onClick={() => setShowFilters(false)}>
                  Cancel
                </button>
                <button className="btn-primary" onClick={applyFilters}>
                  Apply Filters
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="invites-content">
        {error && (
          <div className="error-message">
            <p>Error: {error}</p>
          </div>
        )}

        {loading ? (
          <div className="loading">Loading invites...</div>
        ) : (
          <>
            <div className="invites-table-container">
              <table className="invites-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Description</th>
                    <th>Coupon</th>
                    <th>Created</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invites.map((invite) => (
                    <tr
                      key={invite.id}
                      onClick={(e) => handleRowClick(invite.id, e)}
                      className="invite-row"
                    >
                      <td>
                        <strong>{invite.name}</strong>
                      </td>
                      <td>
                        {invite.description ? (
                          <span>{invite.description.length > 50 ? invite.description.substring(0, 50) + '...' : invite.description}</span>
                        ) : (
                          <span style={{ color: 'var(--text-secondary)' }}>-</span>
                        )}
                      </td>
                      <td>
                        <span className={`coupon-badge ${!invite.coupon ? 'no-coupon' : ''}`}>
                          {formatDiscount(invite)}
                        </span>
                      </td>
                      <td>{formatDate(invite.createdAt)}</td>
                      <td>
                        <span className={`status-badge ${invite.is_active ? 'active' : 'inactive'}`}>
                          {invite.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <div className="actions">
                          <button
                            className="action-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/invites/${invite.id}`);
                            }}
                          >
                            <Edit2 size={14} />
                          </button>
                          <button
                            className="action-btn danger"
                            onClick={(e) => handleDeleteClick(invite, e)}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {invites.length === 0 && !loading && (
              <div className="loading">No invites found</div>
            )}

            <div className="pagination">
              <div className="pagination-info">Showing {invites.length} invites</div>
              <div className="pagination-controls">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="btn-secondary"
                >
                  Previous
                </button>
                <span className="page-info">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((p) => p + 1)}
                  disabled={currentPage >= totalPages}
                  className="btn-secondary"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {showCreateModal && (
        <div className="modal-overlay" onClick={handleCloseCreateModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create Invite</h2>
              <button className="modal-close" onClick={handleCloseCreateModal}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit(onCreateInvite)}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Invite Name *</label>
                  <input
                    type="text"
                    {...register('name')}
                    placeholder="e.g., Beta Tester Invite"
                    className={errors.name ? 'input-error' : ''}
                  />
                  {errors.name && <span className="form-error">{errors.name.message}</span>}
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    {...register('description')}
                    placeholder="Optional description for this invite"
                  />
                </div>

                <div className="form-group">
                  <label>Linked Coupon (optional)</label>
                  <Controller
                    name="coupon_id"
                    control={control}
                    render={({ field }) => (
                      <select
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                      >
                        <option value="">No coupon</option>
                        {coupons.map((coupon) => (
                          <option key={coupon.id} value={coupon.id}>
                            {coupon.name} ({coupon.percent_off ? `${coupon.percent_off}%` : `$${((coupon.amount_off || 0) / 100).toFixed(2)}`} off)
                          </option>
                        ))}
                      </select>
                    )}
                  />
                  <span className="form-hint">If selected, the coupon will be applied when this invite is used</span>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={handleCloseCreateModal}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={createLoading}
                >
                  {createLoading ? 'Creating...' : 'Create Invite'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDeleteModal && inviteToDelete && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Delete Invite</h2>
              <button className="modal-close" onClick={() => setShowDeleteModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="delete-warning">
                <p>Are you sure you want to delete this invite?</p>
                <p>
                  <strong>{inviteToDelete.name}</strong>
                </p>
                <p>This will also remove all user allocations for this invite.</p>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowDeleteModal(false)}>
                Cancel
              </button>
              <button className="btn-danger" onClick={confirmDelete}>
                Delete Invite
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Invites;

